/**
 * Shared zod primitives — mirror openapi.yaml conventions. Verticals build their
 * request/response schemas from these; nothing here is endpoint-specific.
 */
import { z } from "zod";

/** UUID (all ids in the system are UUIDs except pair keys / event_log bigints). */
export const uuid = z.string().uuid();

/**
 * client_event_id — REQUIRED on every client-initiated state-machine mutation
 * (SM §0 idempotency; ARCH §1.2 rule 3). WS twins share the REST endpoint key.
 */
export const clientEventId = uuid;

/** Body shape of the openapi `EventId` request body. */
export const eventIdBody = z.object({ client_event_id: clientEventId });

/** Pair key wire form `${lo}_${hi}` (openapi matchId parameter). */
export const pairKeyWire = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    "matchId must be `${user_lo}_${user_hi}`",
  )
  .refine((s) => {
    const [lo, hi] = s.split("_");
    return lo !== undefined && hi !== undefined && lo < hi;
  }, "pair key must be canonical (lo < hi)");

/** ISO timestamp string (server emits; client never computes outcomes from it). */
export const isoDateTime = z.string().datetime({ offset: true }).or(z.string().datetime());

/** Opaque pagination cursor. */
export const cursor = z.string().min(1).max(512);

/** IANA timezone of record (PRD §7.3) — validated against the runtime's ICU data. */
export const ianaTimezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, "invalid IANA timezone");

/** Date-of-birth: ISO date, must parse, 18+ enforced server-side at the gate. */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD");

/** The normative error envelope (openapi #/components/schemas/Error). */
export const errorEnvelope = z.object({
  code: z.string(),
  message: z.string(),
  request_id: z.string().optional(),
  reset_at: isoDateTime.optional(),
  details: z.record(z.unknown()).optional(),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelope>;

/** WS frame envelope (realtime-protocol §1). */
export const wsFrame = z.object({
  t: z.string().min(1).max(64),
  id: uuid.optional(),
  ts: z.string().optional(),
  p: z.unknown().optional(),
});
export type WsFrame = z.infer<typeof wsFrame>;

/** WS server ack (realtime-protocol §1). */
export const wsAck = z.object({
  t: z.literal("ack"),
  p: z.object({
    for: uuid,
    ok: z.boolean(),
    data: z.unknown().optional(),
    error: z.object({ code: z.string(), message: z.string() }).optional(),
  }),
});
export type WsAck = z.infer<typeof wsAck>;
